import React, { useEffect, useState } from "react";
import {
  Home,
  Search,
  Bell,
  User,
  Plus,
  Upload,
  MapPin,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
  ChevronDown,
  Camera,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  getRequest,
  putRequest,
  putWithFormData,
} from "../../helpers/api.helper";
import { toast } from "react-toastify";
import Gnavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { UPLOAD_URL } from "../../utils/constants";

type BureauFormData = {
  facebook: string;
  instagram: string;
  linkedin: string;
  youtube: string;
  twitter: string;
};

type RawProfile = Partial<BureauFormData> & {
  bureauName?: string | null;
  image?: string | null;
  profile_img?: string | null;
};

const mapProfileToFormData = (profile?: RawProfile | null): BureauFormData => ({
  facebook: profile?.facebook ?? "",
  instagram: profile?.instagram ?? "",
  linkedin: profile?.linkedin ?? "",
  youtube: profile?.youtube ?? "",
  twitter: profile?.twitter ?? "",
});

const EditSocialMediaLinks = () => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<BureauFormData>(
    mapProfileToFormData(bureauInfo)
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    const fetchMyProfile = async () => {
      try {
        const response = await getRequest(`/bureau/me`);
        if (response.success) {
          const profile = response.data?.profile ?? response.data;
          setFormData(mapProfileToFormData(profile));
          setImageRemoved(false);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMyProfile();
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    (Object.keys(formData) as Array<keyof BureauFormData>).forEach((key) => {
      const value = formData[key] ?? "";
      formDataToSend.append(key, value);
    });
    try {
      // @ts-ignore
      const response = await putRequest(`/bureau/me`, formDataToSend);
      if (response.success) {
        toast("Bureau information updated successfully!");
      } else {
        toast.error("Failed to update bureau information.");
      }
    } catch (error) {
      toast.error("There was an error updating the bureau information.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 pb-24">
      {/* Top Navigation */}
      <Gnavbar />

      {/* Main Content */}
      <form
        id="editProfileForm"
        onSubmit={handleSubmit}
        className="max-w-5xl mx-auto px-4 py-6 md:pb-6"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Edit Bureau Profile</h1>
            <p className="text-purple-100 text-sm">
              Update your bureau information and social links
            </p>
          </div>

          <div className="p-6 md:p-8 space-y-6">
            {/* Social Links Section */}
            <div className="pt-6 border-t">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">🔗</span>
                Social Media Links
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Facebook */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Facebook
                  </label>
                  <div className="relative">
                    <Facebook
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600"
                      size={20}
                    />
                    <input
                      type="url"
                      value={formData.facebook}
                      onChange={(e) =>
                        setFormData({ ...formData, facebook: e.target.value })
                      }
                      placeholder="Facebook profile URL"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Instagram */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Instagram
                  </label>
                  <div className="relative">
                    <Instagram
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-600"
                      size={20}
                    />
                    <input
                      type="url"
                      value={formData.instagram}
                      onChange={(e) =>
                        setFormData({ ...formData, instagram: e.target.value })
                      }
                      placeholder="Instagram profile URL"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* LinkedIn */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    LinkedIn
                  </label>
                  <div className="relative">
                    <Linkedin
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-700"
                      size={20}
                    />
                    <input
                      type="url"
                      value={formData.linkedin}
                      onChange={(e) =>
                        setFormData({ ...formData, linkedin: e.target.value })
                      }
                      placeholder="LinkedIn profile URL"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* YouTube */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    YouTube
                  </label>
                  <div className="relative">
                    <Youtube
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-red-600"
                      size={20}
                    />
                    <input
                      type="url"
                      value={formData.youtube}
                      onChange={(e) =>
                        setFormData({ ...formData, youtube: e.target.value })
                      }
                      placeholder="YouTube channel URL"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Twitter */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Twitter
                  </label>
                  <div className="relative">
                    <Twitter
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-500"
                      size={20}
                    />
                    <input
                      type="url"
                      value={formData.twitter}
                      onChange={(e) =>
                        setFormData({ ...formData, twitter: e.target.value })
                      }
                      placeholder="Twitter profile URL"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Update Button - Desktop */}
            <div className="pt-6 hidden md:block">
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
              >
                Update Profile
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Fixed Update Button - Mobile */}
      <div className="md:hidden bottom-16 left-0 right-0 p-4 bg-white border-t shadow-lg z-40">
        <button
          type="submit"
          form="editProfileForm"
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
        >
          Update Profile
        </button>
      </div>

      {/* Bottom Navigation - Mobile */}
      <BottomNavbar />
    </div>
  );
};

export default EditSocialMediaLinks;
